import { runGuardedEffect } from "./ownershipGate";
import { readUploadFacadeErrorDetails } from "./uploadFacadeErrors";
import type { ChunkedUploadDriverPorts, DriverFile, MultipartSessionShape, UploadLease } from "./ports";

/**
 * How many CONSECUTIVE unproductive `UPLOAD_PARTS_INCOMPLETE` refusals one attempt may take before
 * the row is escalated instead of released for yet another identical pass.
 *
 * Small on purpose. A productive refusal — one that names ordinals this client can actually re-mark —
 * resets the counter, so an upload that is genuinely making progress against flaky parts never reaches
 * this bound however many rounds it takes. What reaches it is the shape that cannot make progress.
 */
export const MAX_UNPRODUCTIVE_PARTS_INCOMPLETE_REFUSALS = 3;

/**
 * The refusal reasons that re-marking cannot repair, so re-PUTting the same persisted bytes to the
 * same ordinal reproduces them exactly. Source of truth: `verifyListedParts` in
 * apps/contact-api/src/helpers/chunkedObjectManifest.ts.
 *
 * Deliberately NOT here are the recoverable reasons that helper also returns — `part-missing` and
 * `part-size-mismatch`. The server returns one reason string per refusal for whichever part failed
 * first, so a large upload repairing a different part each round sees the same reason string over
 * and over while genuinely progressing; treating a repeated reason as unproductive would escalate it.
 */
export const PERMANENT_PARTS_INCOMPLETE_REFUSAL_REASONS: ReadonlySet<string> = new Set([
	"part-etag-not-single-put",
	"part-ordinal-duplicate",
	"part-ordinal-out-of-range"
]);

/** Cheap bound on the in-memory ledger: a tab that never reloads must not accumulate rows forever. */
const MAX_TRACKED_ATTEMPTS = 200;

interface PartsIncompleteStreak {
	consecutive: number;
	reason: string | undefined;
	/** The ordinal SET this attempt last re-marked, canonicalised — see `ordinalSetKey`. */
	ordinalSetKey: string | undefined;
}

/**
 * Canonical key for the set of chunk indexes a round re-marked: sorted and de-duplicated, so
 * `[2, 1]` and `[1, 2, 2]` are the same set. Order and duplicates are not information here — what
 * matters is whether the next `/complete` will be asked about a DIFFERENT group of parts.
 */
function ordinalSetKey(chunkIndexes: readonly number[]): string {
	return [...new Set(chunkIndexes)].sort((left, right) => left - right).join(",");
}

/**
 * Keyed by ATTEMPT, not by session: a fresh attempt on the same row (a re-initialised target, a new
 * upload id) is a genuinely different thing to complete and starts with a clean counter.
 *
 * The key also carries the LANE, so the two bounded completion refusals this module tracks
 * (`UPLOAD_PARTS_INCOMPLETE` and `CHUNKED_OBJECT_ASSEMBLY_INTEGRITY`) count independently against the
 * same attempt. They have different bounds and different reset rules, and interleaving them into one
 * counter would let either escalate on the other's evidence.
 */
type RefusalLane = "parts-incomplete" | "assembly-integrity";

const streaksByAttempt = new Map<string, PartsIncompleteStreak>();

function attemptKey(session: MultipartSessionShape, lane: RefusalLane): string {
	return `${lane}::${session.id}::${session.attemptId ?? ""}::${session.uploadId ?? ""}`;
}

function readRefusalReason(error: unknown): string | undefined {
	const reason = readUploadFacadeErrorDetails(error)?.reason;
	return typeof reason === "string" ? reason : undefined;
}

export interface PartsIncompleteRefusalVerdict {
	consecutive: number;
	reason: string | undefined;
	/** True once this attempt has burned MAX_UNPRODUCTIVE_PARTS_INCOMPLETE_REFUSALS without progress. */
	exhausted: boolean;
}

/**
 * Record one `UPLOAD_PARTS_INCOMPLETE` refusal against its attempt and say whether the lane has run
 * out of reasons to keep retrying.
 *
 * A refusal counts as UNPRODUCTIVE when either
 *   (a) it re-marked zero chunks — nothing about the next `/complete` body will differ, or
 *   (b) its reason is one of `PERMANENT_PARTS_INCOMPLETE_REFUSAL_REASONS` — re-uploading the named
 *       ordinal reproduces the same verdict, so the re-mark is not progress.
 * Anything else — any refusal that re-marked at least one chunk under a recoverable reason — resets
 * the streak, however many times that reason repeats.
 */
export function recordPartsIncompleteRefusal(input: {
	session: MultipartSessionShape;
	error: unknown;
	chunkIndexes: readonly number[];
	/**
	 * Did `applyPartsIncompleteRemark` actually change the ledger this round? `false` for a missing
	 * `markChunksNotUploaded` port and for a CAS that modified zero rows — both of which name ordinals
	 * while changing nothing, and both of which used to reset the streak and make the bound unreachable.
	 * Left `undefined` by callers that cannot tell, which keeps the pre-existing behaviour.
	 */
	remarkApplied?: boolean;
}): PartsIncompleteRefusalVerdict {
	const key = attemptKey(input.session, "parts-incomplete");
	const previous = streaksByAttempt.get(key);
	const reason = readRefusalReason(input.error);
	const remarkedNothing = input.chunkIndexes.length === 0;
	const isPermanentReason = reason !== undefined && PERMANENT_PARTS_INCOMPLETE_REFUSAL_REASONS.has(reason);
	const setKey = ordinalSetKey(input.chunkIndexes);
	// Productivity is a property of the ORDINAL SET, not of the reason string. A server that refuses the
	// same ordinal for the same recoverable reason round after round changes nothing about the next
	// `/complete`, but the old "any ordinal named = progress" rule reset the streak every time, so the
	// bound could not fire. A DIFFERENT set is still real progress, however often the reason repeats.
	const repeatedSameSet = previous !== undefined && previous.ordinalSetKey === setKey && previous.reason === reason;
	const unproductive = remarkedNothing || isPermanentReason || input.remarkApplied === false || repeatedSameSet;
	const consecutive = unproductive ? (previous?.consecutive ?? 0) + 1 : 0;
	if (streaksByAttempt.size >= MAX_TRACKED_ATTEMPTS && !streaksByAttempt.has(key)) {
		const oldest = streaksByAttempt.keys().next();
		if (!oldest.done) streaksByAttempt.delete(oldest.value);
	}
	streaksByAttempt.set(key, { consecutive, reason, ordinalSetKey: setKey });
	// `>=`, not `>`: `consecutive` counts this refusal, so `>` would allow a fourth pass and
	// contradict the bound's own documentation.
	return { consecutive, reason, exhausted: consecutive >= MAX_UNPRODUCTIVE_PARTS_INCOMPLETE_REFUSALS };
}

/** Forget an attempt's streak — called once the row is no longer going to re-complete. */
export function clearPartsIncompleteRefusals(session: MultipartSessionShape): void {
	streaksByAttempt.delete(attemptKey(session, "parts-incomplete"));
}

/**
 * How many CONSECUTIVE `CHUNKED_OBJECT_ASSEMBLY_INTEGRITY` failures one attempt may take before the
 * row is escalated instead of released for yet another identical `/complete`.
 *
 * Review finding 14. Both drivers classify this code as retryable, which is correct for ONE retry:
 * the parts are kept server-side and a transient copy fault can genuinely verify on the next pass.
 * But every retry re-runs a FULL server-side assembly — one UploadPartCopy per part, minutes of S3
 * work on a multi-GiB upload — and nothing counted the failures, so a deterministic mismatch (a part
 * rewritten between verification and copy, an SSE-KMS ETag the predictor cannot reproduce) spent that
 * cost forever and never reached the user.
 *
 * Two, not three: unlike a parts-incomplete refusal there is nothing the client re-marks between
 * rounds, so the second attempt is already the identical request. One retry buys the transient case;
 * a second identical failure is evidence, not noise.
 */
export const MAX_CONSECUTIVE_ASSEMBLY_INTEGRITY_FAILURES = 2;

/**
 * The reason label stamped on an assembly-integrity verdict. The server sends no `reason` for this
 * code (the message carries the mismatch), so the lane supplies its own rather than reporting
 * `undefined` in the escalation log and telemetry.
 */
export const ASSEMBLY_INTEGRITY_REFUSAL_REASON = "chunked-object-assembly-integrity";

/**
 * Record one assembly-integrity failure against its attempt and say whether the lane is out of
 * retries. Every occurrence is unproductive by construction — the client changes nothing between
 * rounds — so the streak only ever grows here, and is cleared by an actual success
 * (`clearAssemblyIntegrityFailures`, called when the row reaches terminal-complete).
 */
export function recordAssemblyIntegrityFailure(input: { session: MultipartSessionShape; error: unknown }): PartsIncompleteRefusalVerdict {
	const key = attemptKey(input.session, "assembly-integrity");
	const previous = streaksByAttempt.get(key);
	const reason = readRefusalReason(input.error) ?? ASSEMBLY_INTEGRITY_REFUSAL_REASON;
	const consecutive = (previous?.consecutive ?? 0) + 1;
	if (streaksByAttempt.size >= MAX_TRACKED_ATTEMPTS && !streaksByAttempt.has(key)) {
		const oldest = streaksByAttempt.keys().next();
		if (!oldest.done) streaksByAttempt.delete(oldest.value);
	}
	streaksByAttempt.set(key, { consecutive, reason, ordinalSetKey: previous?.ordinalSetKey });
	return { consecutive, reason, exhausted: consecutive >= MAX_CONSECUTIVE_ASSEMBLY_INTEGRITY_FAILURES };
}

/** Forget an attempt's assembly-integrity streak — called once a completion actually succeeded. */
export function clearAssemblyIntegrityFailures(session: MultipartSessionShape): void {
	streaksByAttempt.delete(attemptKey(session, "assembly-integrity"));
}

/** Test-only reset so one spec's streaks cannot leak into the next. */
export function resetPartsIncompleteRefusalLedger(): void {
	streaksByAttempt.clear();
}

/**
 * The terminal arm for a parts-incomplete refusal the client cannot act on: run the app's own
 * `classifyFailure` (so its telemetry and bucketing still see the error) and mark the row
 * needs-attention, which is the only outcome that actually surfaces to a human.
 *
 * Always resolves — never rethrows. Losing the escalation write means another owner or a more
 * authoritative verdict won the race, which is exactly what `markNeedsAttention`'s CAS is for.
 */
export async function escalateUnproductivePartsIncomplete<
	FileLike extends DriverFile,
	Session extends MultipartSessionShape,
	LeaseToken
>(input: {
	session: Session;
	error: unknown;
	lease: UploadLease<Session>;
	ports: ChunkedUploadDriverPorts<FileLike, Session, LeaseToken>;
	verdict: PartsIncompleteRefusalVerdict;
	/** Which bounded refusal ran out. Defaults to the parts-incomplete lane this helper was minted for. */
	lane?: RefusalLane;
}): Promise<"yielded"> {
	const { session, error, ports, verdict } = input;
	const lane: RefusalLane = input.lane ?? "parts-incomplete";
	const bound = lane === "assembly-integrity" ? MAX_CONSECUTIVE_ASSEMBLY_INTEGRITY_FAILURES : MAX_UNPRODUCTIVE_PARTS_INCOMPLETE_REFUSALS;
	const message = ports.errors.normalize(error).message;
	let classified: string | undefined;
	try {
		classified = ports.persistedResume.classifyFailure(error);
	} catch {
		// The app classifier is diagnostic here; its failure must not keep the row looping.
	}
	// CLAUDE.md "Diagnostic logging when fixing silent failures": before this line a permanently
	// refused completion was released back to pending with no record that it had already been refused
	// N times, so the loop was invisible in every log.
	console.warn(
		lane === "assembly-integrity"
			? "[Upload] Chunked-object assembly failed its integrity check too many times; escalating to needs-attention"
			: "[Upload] Chunked-object completion refused unproductively too many times; escalating to needs-attention",
		{
			lane,
			sessionId: session.id,
			uploadId: session.uploadId,
			registryId: session.registryId,
			attemptId: session.attemptId,
			reason: verdict.reason,
			consecutiveRefusals: verdict.consecutive,
			bound,
			classifiedAs: classified,
			message
		}
	);
	// The counters are cleared ONLY below, after a confirmed write. Clearing them here — which this
	// function used to do — was the AI reviewer's MEDIUM: a throwing `markNeedsAttention` was swallowed
	// and "yielded" returned with the streak already at zero, so the lease-TTL re-drive restarted the
	// identical refusal from zero and the bound could never actually bind.
	let escalationWritten = false;
	try {
		const writeOutcome = await runGuardedEffect(input.lease, session, () =>
			ports.sessionStore.markNeedsAttention(session, message, error)
		);
		escalationWritten = writeOutcome !== "skipped-not-owned";
		if (!escalationWritten) {
			// Another owner holds the row; its counters are ours to keep, and releasing a row we do not
			// own is not ours to do either. Named so "escalated" and "someone else owns it" stay apart.
			console.warn("[Upload] Chunked-object escalation skipped: the lease is owned by another tab", {
				lane,
				sessionId: session.id,
				uploadId: session.uploadId,
				attemptId: session.attemptId,
				reason: verdict.reason
			});
		}
	} catch (escalationError) {
		try {
			ports.telemetry.logWarn(
				"recording.fileUpload.partsIncompleteEscalationFailed",
				{
					lane,
					sessionId: session.id,
					uploadId: session.uploadId,
					reason: verdict.reason
				},
				escalationError
			);
		} catch {
			// Escalation diagnostics must never replace the originating refusal.
		}
		// CLAUDE.md "Diagnostic logging when fixing silent failures": the throw itself is the silent
		// condition — before this the row simply looped, with nothing naming the failed write.
		console.warn("[Upload] Chunked-object escalation write failed; releasing the row for retry with its streak intact", {
			lane,
			sessionId: session.id,
			uploadId: session.uploadId,
			attemptId: session.attemptId,
			reason: verdict.reason,
			consecutiveRefusals: verdict.consecutive,
			bound
		});
		// Fall through to a release rather than reporting an escalation that did not happen. The streak
		// is deliberately NOT cleared, so the next pass is already at the bound and escalates again as
		// soon as the write succeeds — bounded either way.
		try {
			await runGuardedEffect(input.lease, session, () => ports.sessionStore.releasePending(session, message));
		} catch (releaseError) {
			try {
				ports.telemetry.logWarn(
					"recording.fileUpload.partsIncompleteEscalationReleaseFailed",
					{ lane, sessionId: session.id, uploadId: session.uploadId, reason: verdict.reason },
					releaseError
				);
			} catch {
				// Nothing left to report it with; the lease TTL still re-drives the row.
			}
		}
		return "yielded";
	}
	if (escalationWritten) {
		if (lane === "assembly-integrity") clearAssemblyIntegrityFailures(session);
		else clearPartsIncompleteRefusals(session);
	}
	return "yielded";
}
